import React, { useState } from 'react';
import { TrendingDown, DollarSign, Zap, Shield, Users, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import JsonLd, { getBaseUrl } from '../components/JsonLd';
import ContactModal from '../components/ContactModal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const WhyUs = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const benefits = [
    {
      icon: TrendingDown,
      title: 'Efektyvumas ir nauda',
      points: [
        'Nereikia investuoti į sandėlį ir personalą',
        'Mokate tik už faktiškai suteiktas paslaugas',
        'Lankstūs sprendimai pagal verslo apimtis',
        'Aiškios išklotinės ir sąžiningi įkainiai'
      ],
      color: 'blue'
    },
    {
      icon: Shield,
      title: 'Saugumas ir patikimumas',
      points: [
        'Aptverta ir saugoma teritorija',
        'Signalizacija 24/7',
        'Prekių draudimas',
        'Moderni WMS sistema'
      ],
      color: 'green'
    },
    {
      icon: Zap,
      title: 'Greitis ir lankstumas',
      points: [
        'Greita reakcija į jūsų poreikius',
        'Lankstūs sprendimai',
        'Operatyvus aptarnavimas',
        'Prisitaikome prie sezoniškumo'
      ],
      color: 'yellow'
    },
    {
      icon: Users,
      title: 'Profesionalumas',
      points: [
        'Patyrę logistikos specialistai',
        'Individuali paskyra kiekvienam klientui',
        'Profesionali įranga',
        'Nuolatinis kvalifikacijos kėlimas'
      ],
      color: 'purple'
    },
    {
      icon: Clock,
      title: 'Skaidrumas ir kontrolė',
      points: [
        'Realaus laiko ataskaitos',
        'Pilna atsargų kontrolė',
        'Nuolatinis informavimas',
        'Aiškūs procesai'
      ],
      color: 'red'
    },
    {
      icon: DollarSign,
      title: 'Aiškus įkainojimas',
      points: [
        'Minimali mėnesio suma – nuo 300 EUR',
        'Nėra paslėptų mokesčių',
        'Lanksti kainodara',
        'Individualūs pasiūlymai'
      ],
      color: 'indigo'
    },
  ];

  const faqItems = [
    {
      question: 'Ar teikiate trumpalaikį ir ilgalaikį sandėliavimą?',
      answer: 'Taip. Teikiame tiek trumpalaikį, tiek ilgalaikį sandėliavimą, pritaikydami sprendimą pagal prekių srautus ir Jūsų verslo poreikius.'
    },
    {
      question: 'Kaip vyksta prekių priėmimas į sandėlį?',
      answer: 'Prekės priimamos pagal suderintą atvežimo laiką: atliekame iškrovimą, patikriname kiekį ir būklę, suženkliname (jei reikia) ir užregistruojame sandėlio valdymo sistemoje (WMS).'
    },
    {
      question: 'Ar atliekate komplektavimą ir prekių paruošimą siuntoms?',
      answer: 'Taip. Atliekame prekių atrinkimą, komplektavimą, perpakavimą, ženklinimą, svėrimą ir kitus paruošimo darbus pagal Jūsų reikalavimus.'
    },
    {
      question: 'Ar taikote FIFO arba FEFO metodus?',
      answer: 'Taip. Užsakymų atrinkimą galime vykdyti taikant FIFO arba FEFO metodus, priklausomai nuo prekių specifikos ir Jūsų procesų.'
    },
    {
      question: 'Kaip skaičiuojama paslaugų kaina?',
      answer: 'Kaina priklauso nuo paslaugų apimčių ir procesų sudėtingumo (sandėliavimas, komplektavimas, krovos darbai, ataskaitos, 3PL). Minimali mėnesio paslaugų suma – nuo 300 EUR. Tikslų pasiūlymą pateikiame gavę Jūsų užklausą.'
    }
  ];

  const baseUrl = getBaseUrl();
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${baseUrl}kodel-mes#faq`,
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };

  return (
    <div className="min-h-screen" data-testid="why-us-page">
      <SEO 
        title="Kodėl rinktis WSK Logistiką? | Efektyvūs 3PL sprendimai"
        description="Optimizuokite logistikos sąnaudas be nuosavo sandėlio. Skaidrūs įkainiai, lankstūs sprendimai ir patikimas logistikos partneris Kaune."
        keywords="efektyvi logistika, 3PL sprendimai, logistikos optimizavimas, sandėliavimo paslaugos, skaidrūs įkainiai, lankstūs sprendimai"
      />
      <JsonLd id="faq" data={faqJsonLd} />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Kodėl rinktis mus?</h1>
            <p className="text-xl text-blue-100">
              Mūsų patirtis, profesionalumas ir individuali prieiga – tai, kas padeda jūsų verslui augti
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Jūsų pranašumai dirbant su mumis
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Kompleksiniai sprendimai, kurie padeda jums sutaupyti laiką ir pinigus
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-8"
                  data-testid={`benefit-detail-${index}`}
                >
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center bg-blue-600 p-4 rounded-xl">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <ul className="space-y-3">
                    {benefit.points.map((point, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold mr-3 mt-0.5">
                          ✓
                        </span>
                        <span className="text-gray-600">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Aiškus įkainojimas</h3>
            <p className="text-xl text-blue-100 mb-4">
              Minimali mėnesio paslaugų suma – <strong className="text-white">nuo 300 EUR</strong>
            </p>
            <p className="text-blue-100">
              Aiškios išklotinės ir sąžiningi įkainiai. Nėra paslėptų mokesčių.
              Mokate tik už faktiškai suteiktas paslaugas.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Dažniausiai užduodami klausimai</h2>
            <p className="text-lg text-gray-600">
              Trumpi atsakymai į dažniausius klausimus apie mūsų paslaugas
            </p>
          </div>
          <Accordion
            type="single"
            collapsible
            defaultValue="faq-0"
            className="rounded-2xl border border-blue-100 bg-white/90 shadow-lg backdrop-blur"
          >
            {faqItems.map((item, index) => (
              <AccordionItem
                key={item.question}
                value={`faq-${index}`}
                data-testid={`faq-item-${index}`}
                className="border-blue-100"
              >
                <AccordionTrigger className="px-6 text-base font-semibold text-gray-900 transition-colors hover:text-blue-700 focus:outline-none focus-visible:ring-0 data-[state=open]:text-blue-700">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 text-gray-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Why Choose Us Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Strateginė partnerystė jūsų verslui
              </h2>
              <div className="space-y-6">
                <div className="border-l-4 border-blue-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Koncentruokitės į savo verslą</h3>
                  <p className="text-gray-600">
                    Mes perimame visus logistikos procesus, kad jūs galėtumėte susitelkti į savo
                    pagrindinės veiklos plėtrą ir augimą.
                  </p>
                </div>
                <div className="border-l-4 border-blue-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Individualūs sprendimai</h3>
                  <p className="text-gray-600">
                    Kiekvienas verslas yra unikalus. Pritaikome mūsų paslaugas būtent jūsų
                    specifiniams poreikiams ir tikslams.
                  </p>
                </div>
                <div className="border-l-4 border-blue-600 pl-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Ilgalaikė partnerystė</h3>
                  <p className="text-gray-600">
                    Ieškome ilgalaikių santykių su klientais. Jūsų sėkmė – mūsų sėkmė.
                  </p>
                </div>
              </div>
            </div>
            <div className="relative h-96 rounded-xl overflow-hidden shadow-xl">
              <img
                src="/images/photo-1645220127753-e9282320bb56.jpeg"
                alt="Professional partnership"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Pasiruošę pradėti bendradarbiavimą?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Susisiekite su mumis ir gaukite individualų pasiūlymą
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-xl"
            data-testid="why-us-cta-contact"
          >
            Gauti pasiūlymą
          </button>
        </div>
      </section>
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default WhyUs;
